/**
 * What each signed-in route needs, started as early as the session allows
 * (BEN-141 QF6).
 *
 * Two sets, and the split is the whole point.
 *
 * THE SHELL SET is what every app route fetches no matter which page it is:
 * the profile the onboarding gate reads, the subscription state the gate and
 * the trial banner read, the accounts the welcome guide reads, and the alerts
 * the notification bell reads. Four requests, one dependency between them — the
 * Clerk session — and the component tree happened to serialise them into three
 * rounds. Nothing here is ordered, so nothing here is started in an order.
 *
 * THE ROUTE SET is the page's own read. It waited third only because
 * `SubscriptionGate` withholds its `<Outlet/>` while the status call is in
 * flight, and no page query depends on the subscription state: the server, not
 * this gate, is what refuses data to an unentitled user (`HasActiveSubscription`
 * answers 402 regardless of what the client decided). Starting it beside the
 * shell set is therefore not a race, it is the removal of one.
 *
 * WHAT IS DELIBERATELY ABSENT.
 *   - `/transactions`, `/budgets` and `/calendar` read month- or filter-scoped
 *     queries whose parameters are page state (`Budgets` picks the month,
 *     `Transactions` the filters). Prefetching a guess at those parameters
 *     fetches a key the page will not read and leaves the page's real request
 *     exactly where it was, so it is worse than not prefetching. `/calendar`
 *     still gets its two unparameterised forecast reads.
 *   - `/recurring` fetches with `useEffect` + `useState` rather than React
 *     Query, so there is no key to warm. That page owns its own fix.
 *   - Mutations, obviously, and anything a dialog opens.
 */
import type { FetchQueryOptions } from "@tanstack/react-query";

import { FEATURE_FLAGS } from "@/config/featureFlags";
import {
  bankAccountsQuery,
  categoriesQuery,
  categoryRulesQuery,
  dashboardAlertsQuery,
  debtOverviewQuery,
  financialAnalysisQuery,
  forecastPeriodsQuery,
  forecastPlanQuery,
  goalsOverviewQuery,
  insightsQuery,
  paycheckQuery,
  profileQuery,
  subscriptionStatusQuery,
} from "@/lib/appQueries";

/* eslint-disable @typescript-eslint/no-explicit-any -- a heterogeneous list of
   descriptors has no useful common data type; `prefetchQuery` only reads the
   key, the fetcher and the stale window, all of which are typed on each
   descriptor at its definition. */
type Prefetchable = FetchQueryOptions<any, any, any, any>;
/* eslint-enable @typescript-eslint/no-explicit-any */

/** Read by every app route, whichever page it is. */
export function appShellQueries(): Prefetchable[] {
  const queries: Prefetchable[] = [profileQuery, subscriptionStatusQuery, bankAccountsQuery];
  // The bell renders mock alerts when the flag is off and asks for none.
  if (FEATURE_FLAGS.DASHBOARD_REAL_API) queries.push(dashboardAlertsQuery);
  return queries;
}

/** Read by one page, keyed by the pathname `OnboardingGate` was entered on. */
export function routeQueries(pathname: string): Prefetchable[] {
  switch (pathname) {
    case "/forecast":
      return [forecastPlanQuery];
    case "/calendar":
      return [forecastPlanQuery, forecastPeriodsQuery()];
    case "/goals":
      return [goalsOverviewQuery, forecastPlanQuery];
    case "/debt":
      return [debtOverviewQuery];
    case "/paycheck":
      return [paycheckQuery()];
    case "/financial-analysis":
      return [financialAnalysisQuery];
    case "/rules":
    case "/settings/rules":
      return [categoriesQuery, categoryRulesQuery];
    case "/insights":
      return FEATURE_FLAGS.INSIGHTS_REAL_API ? [insightsQuery] : [];
    default:
      return [];
  }
}
